import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import { configureApiClient } from '@cardioq/api-client';
import App from './App';
import { authTokenStore, useAuthStore } from './hooks/useAuth';
import { requestStepUp } from './insurance/lib/stepUp';
import './index.css';

// Base path the app is served under. Vite sets BASE_URL from the build
// `base` config: '/' in local dev, '/patient/' in the deployed image
// (so it can live alongside the dashboard SPA on the same origin).
// React Router's basename must not carry a trailing slash (except root).
const BASE = import.meta.env.BASE_URL.replace(/\/$/, '');
const ROUTER_BASENAME = BASE || '/';

// Wire the shared axios client to the patient app's auth store so the
// bearer-JWT interceptor reads from the same source of truth. On 401,
// clear tokens and route the user back to /login (handled by the
// ProtectedRoute on the next render after the redirect).
configureApiClient({
  baseURL: import.meta.env.VITE_API_BASE ?? '/api/v1',
  apiKey: import.meta.env.VITE_API_KEY ?? 'dev-api-key-change-me',
  tokenStore: authTokenStore,
  onUnauthenticated: () => {
    // Use window.location to bypass React Router — we don't have access
    // to the navigate function from this boot context, and a full
    // navigation here is appropriate (it clears in-memory state too).
    // Prefix with BASE so the redirect lands inside the patient app
    // (/patient/login) rather than the dashboard's /login.
    const loginPath = `${BASE}/login`;
    if (window.location.pathname !== loginPath) {
      window.location.assign(loginPath);
    }
  },
  // T22 — a decide/settle answered 401 step_up_required: open the one
  // StepUpModal (mounted in InsuranceApp); concurrent calls share it.
  onStepUpRequired: requestStepUp,
});

// Apply persisted theme synchronously before first paint so there's no
// dark/light flash. The useTheme hook keeps it in sync once mounted.
(function applyPersistedTheme() {
  try {
    const stored = localStorage.getItem('cardioq-patient-theme');
    const root = document.documentElement;
    const initial =
      stored === 'light' || stored === 'dark'
        ? stored
        : window.matchMedia?.('(prefers-color-scheme: light)').matches
          ? 'light'
          : 'dark';
    root.classList.remove('dark', 'light');
    root.classList.add(initial);
    root.setAttribute('data-theme', initial);
  } catch {
    /* localStorage unavailable — defaults to the HTML's class='dark' */
  }
})();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 60_000,
    },
  },
});

// Drop the entire query cache whenever the authenticated subject changes
// (logout, login as someone else, or the register wizard's auto-login).
// Without this, the previous user's cached responses — consents, profile,
// scores — are served to the next user (e.g. Home flashing another
// patient's data, or a stale consent state).
let lastAuthSubject = useAuthStore.getState().claims?.sub ?? null;
useAuthStore.subscribe((state) => {
  const subject = state.claims?.sub ?? null;
  if (subject !== lastAuthSubject) {
    lastAuthSubject = subject;
    queryClient.clear();
  }
});

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element #root not found');
}

ReactDOM.createRoot(rootElement).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter basename={ROUTER_BASENAME}>
        <App />
        <Toaster
          theme="dark"
          position="top-right"
          richColors
          closeButton
        />
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>,
);
